/**
 * utmTracking
 *
 * Capture l'attribution marketing (paramètres UTM + page d'arrivée + référent)
 * au premier chargement, la persiste dans `localStorage`, puis la rattache à
 * `auth.users.raw_user_meta_data` au moment de l'inscription (cf.
 * AuthContext.signUp). Le trigger SQL `handle_new_user` recopie ces champs dans
 * `profiles` pour permettre l'analyse du tunnel acquisition → conversion.
 *
 * Base légale : intérêt légitime (RGPD art. 6.1.f). Données 1st-party, non
 * revendues, sans profilage publicitaire ni traçage cross-site. Couvert par la
 * politique de confidentialité (section "attribution").
 *
 * Attribution "first-touch" : on ne réécrit jamais un enregistrement existant,
 * afin de créditer la source d'origine et non le dernier clic.
 */

const STORAGE_KEY = "dd_attribution";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
] as const;

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  landing_path?: string;
  referrer?: string;
  captured_at?: string;
};

/**
 * Lit les paramètres UTM de l'URL courante et, si présents ET qu'aucune
 * attribution n'a déjà été enregistrée, persiste l'enregistrement (first-touch).
 * Idempotent et sans effet si aucun UTM n'est présent. Échoue silencieusement
 * (navigation privée / quota localStorage).
 */
export function captureAttribution(): void {
  try {
    if (typeof window === "undefined") return;

    // First-touch : ne jamais écraser un enregistrement existant.
    if (localStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const record: Attribution = {};
    let hasUtm = false;

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) {
        record[key] = value.slice(0, 255);
        hasUtm = true;
      }
    }

    // Pas de tag UTM → rien à attribuer.
    if (!hasUtm) return;

    record.landing_path = window.location.pathname.slice(0, 255);
    record.referrer = (document.referrer || "").slice(0, 255);
    record.captured_at = new Date().toISOString();

    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // navigation privée / quota dépassé : on ignore, l'utilisateur reste
    // simplement non-attribué (aucune erreur visible).
  }
}

/**
 * Retourne l'attribution persistée, ou `null` si absente / corrompue.
 */
export function getAttribution(): Attribution | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as Attribution;
  } catch {
    return null;
  }
}

/**
 * Supprime l'attribution persistée. À appeler après une inscription réussie
 * pour éviter qu'une seconde inscription sur le même appareil (poste partagé)
 * ne soit taguée avec la source du premier utilisateur.
 */
export function clearAttribution(): void {
  try {
    if (typeof window === "undefined") return;
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
